import React, { useEffect, useState, useRef } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ATT_STATUS } from "@/lib/constants";
import { toast } from "sonner";
import { Check, X, FileText, Thermometer, Loader2, Maximize2, Smile, UserX, WifiOff, CloudUpload, RefreshCw, Clock, QrCode, ScanLine, Square, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useOfflineQueue, readQueue, enqueueAttendance, flushQueue, isNetworkError, cacheRoster, readRoster } from "@/lib/offlineQueue";
import { Html5Qrcode } from "html5-qrcode";

function PhotoVerify() {
  const todayWib = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" });
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(todayWib);
  const [lightbox, setLightbox] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/attendance", { params: date ? { date } : {} }).then((r) => setList(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [date]);

  const review = async (id, status, reason = "") => {
    await api.put(`/attendance/${id}/review`, { status, reason });
    toast.success(`Absensi ditandai: ${ATT_STATUS[status].label}`);
    load();
  };

  const pending = list.filter((a) => a.status === "menunggu");
  const done = list.filter((a) => a.status !== "menunggu");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end gap-2">
        <span className="text-sm text-slate-500">Tanggal</span>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44 rounded-xl" data-testid="attendance-date-filter" />
        {date !== todayWib && <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setDate(todayWib)} data-testid="attendance-today-btn">Hari Ini</Button>}
        {date && <Button variant="ghost" size="sm" className="rounded-xl text-slate-500" onClick={() => setDate("")} data-testid="attendance-show-all-btn">Semua Tanggal</Button>}
      </div>
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
      ) : (
        <>
          <section>
            <h2 className="font-display text-lg font-semibold text-slate-800 mb-3">Menunggu Verifikasi ({pending.length})</h2>
            {pending.length === 0 ? (
              <p className="text-sm text-slate-400 bg-white rounded-2xl p-6 border border-slate-100">Tidak ada absensi foto yang menunggu.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
                {pending.map((a) => (
                  <div key={a.id} className="bg-white rounded-2xl p-4 border border-amber-100 shadow-sm" data-testid={`attendance-card-${a.id}`}>
                    <div className="relative">
                      <img src={fileUrl(a.photo_path)} alt={a.student_name} className="w-full h-44 object-cover rounded-xl bg-slate-100" />
                      <button onClick={() => setLightbox(fileUrl(a.photo_path))} className="absolute top-2 right-2 bg-black/50 text-white p-1.5 rounded-lg"><Maximize2 className="w-4 h-4" /></button>
                    </div>
                    <div className="mt-3">
                      <p className="font-semibold text-slate-800">{a.student_name}</p>
                      <p className="text-xs text-slate-500">Kelas {a.kelas}{a.rombel || ""} · {a.time} WIB · diminta: {a.requested_status}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <Button size="sm" className="rounded-lg bg-emerald-600 hover:bg-emerald-700 col-span-2" onClick={() => review(a.id, "hadir")} data-testid={`attendance-approve-btn-${a.id}`}><Check className="w-4 h-4 mr-1" /> Setujui (Hadir)</Button>
                      <Button size="sm" variant="outline" className="rounded-lg text-sky-700" onClick={() => review(a.id, "izin")} data-testid={`attendance-izin-btn-${a.id}`}><FileText className="w-4 h-4 mr-1" /> Izin</Button>
                      <Button size="sm" variant="outline" className="rounded-lg text-orange-700" onClick={() => review(a.id, "sakit")} data-testid={`attendance-sakit-btn-${a.id}`}><Thermometer className="w-4 h-4 mr-1" /> Sakit</Button>
                      <Button size="sm" variant="outline" className="rounded-lg text-rose-700 col-span-2" onClick={() => review(a.id, "ditolak")} data-testid={`attendance-reject-btn-${a.id}`}><X className="w-4 h-4 mr-1" /> Tolak</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold text-slate-800 mb-3">Sudah Diverifikasi ({done.length})</h2>
            <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-50">
              {done.length === 0 && <p className="p-6 text-sm text-slate-400">Belum ada.</p>}
              {done.map((a) => {
                const st = ATT_STATUS[a.status] || ATT_STATUS.menunggu;
                return (
                  <div key={a.id} className="flex items-center gap-3 px-4 py-3">
                    {a.photo_path ? <img src={fileUrl(a.photo_path)} alt="" className="w-10 h-10 rounded-full object-cover bg-slate-100" /> : <div className="w-10 h-10 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-sm font-semibold">{a.student_name?.[0]}</div>}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{a.student_name}{a.manual ? " · (tatap muka)" : ""}</p>
                      <p className="text-xs text-slate-400">Kelas {a.kelas} · {a.date} · {a.time}</p>
                    </div>
                    <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${st.cls}`}>{st.label}</span>
                  </div>
                );
              })}
            </div>
          </section>
        </>
      )}

      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="rounded-2xl p-2 max-w-xl">{lightbox && <img src={lightbox} alt="" className="w-full rounded-xl" />}</DialogContent>
      </Dialog>
    </div>
  );
}

const QUICK = [
  { v: "hadir", label: "Hadir", icon: Smile, cls: "bg-emerald-600 hover:bg-emerald-700 text-white" },
  { v: "izin", label: "Izin", icon: FileText, cls: "bg-sky-600 hover:bg-sky-700 text-white" },
  { v: "sakit", label: "Sakit", icon: Thermometer, cls: "bg-orange-600 hover:bg-orange-700 text-white" },
  { v: "alpa", label: "Alpa", icon: UserX, cls: "bg-rose-600 hover:bg-rose-700 text-white" },
];

function OfflineRoster() {
  const { user } = useAuth();
  const wali = user?.teacher_type === "wali" && user?.wali_kelas;
  const [kelas, setKelas] = useState(wali ? String(user.wali_kelas) : "1");
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" });
  const [date, setDate] = useState(today);
  const [students, setStudents] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const { queue, online, syncing } = useOfflineQueue();

  const applyQueued = (m) => {
    readQueue().filter((q) => q.date === date).forEach((q) => { m[q.student_id] = q.status; });
    return m;
  };

  const load = () => {
    setLoading(true);
    setFromCache(false);
    Promise.all([
      api.get("/students", { params: { kelas: parseInt(kelas) } }),
      api.get("/attendance", { params: { kelas: parseInt(kelas), date } }),
    ]).then(([s, a]) => {
      setStudents(s.data);
      const m = {}; a.data.forEach((x) => { m[x.student_id] = x.status; });
      cacheRoster(kelas, date, s.data, m);
      setStatuses(applyQueued(m));
    }).catch(() => {
      const c = readRoster(kelas, date);
      if (c) { setStudents(c.students); setStatuses(applyQueued({ ...c.statuses })); setFromCache(true); }
      else { toast.error("Tidak ada koneksi dan belum ada data tersimpan untuk kelas ini"); }
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [kelas, date]);

  const sync = async () => {
    const r = await flushQueue();
    if (r.sent) { toast.success(`${r.sent} absensi offline berhasil dikirim`); load(); }
    if (r.failed) toast.warning(`${r.failed} absensi masih menunggu koneksi`);
  };
  useEffect(() => {
    if (!online) return;
    if (readQueue().length) sync(); else if (fromCache) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  const mark = async (studentId, status) => {
    if (statuses[studentId] === status) { return clearStatus(studentId); }
    setSaving(studentId + status);
    const st = students.find((s) => s.id === studentId);
    const saveOffline = () => {
      enqueueAttendance({ student_id: studentId, student_name: st?.name, status, date, kelas });
      setStatuses((m) => ({ ...m, [studentId]: status }));
      toast.info(`Sinyal hilang — ${st?.name || "absensi"} disimpan offline, akan dikirim otomatis`, { duration: 4000 });
    };
    if (!navigator.onLine) { saveOffline(); setSaving(null); return; }
    try {
      await api.post("/attendance/manual", { student_id: studentId, status, date });
      setStatuses((m) => ({ ...m, [studentId]: status }));
      toast.success(`Ditandai ${status}`);
    } catch (e) {
      if (isNetworkError(e)) saveOffline(); else toast.error("Gagal menandai");
    } finally { setSaving(null); }
  };

  const clearStatus = async (studentId) => {
    setSaving(studentId + "clear");
    const st = students.find((s) => s.id === studentId);
    const clearLocal = () => setStatuses((m) => { const n = { ...m }; delete n[studentId]; return n; });
    const saveOffline = () => {
      enqueueAttendance({ student_id: studentId, student_name: st?.name, status: "_clear", date, kelas });
      clearLocal();
      toast.info(`Sinyal hilang — penghapusan status ${st?.name || ""} disimpan, dikirim otomatis`, { duration: 4000 });
    };
    if (!navigator.onLine) { saveOffline(); setSaving(null); return; }
    try {
      await api.delete("/attendance/manual", { params: { student_id: studentId, date } });
      clearLocal();
      toast.success("Status absen dihapus");
    } catch (e) {
      if (isNetworkError(e)) saveOffline(); else toast.error("Gagal menghapus status");
    } finally { setSaving(null); }
  };

  const pendingHere = queue.filter((q) => q.date === date && String(q.kelas) === String(kelas));

  return (
    <div className="space-y-5">
      {(!online || queue.length > 0 || fromCache) && (
        <div className={`rounded-2xl p-4 border flex flex-wrap items-center gap-3 ${online ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-slate-800 border-slate-700 text-white"}`} data-testid="offline-status-bar">
          {online ? <CloudUpload className="w-5 h-5 shrink-0" /> : <WifiOff className="w-5 h-5 shrink-0" />}
          <div className="flex-1 min-w-[200px] text-sm">
            {!online && <p className="font-semibold">Mode Offline — tidak ada koneksi internet</p>}
            {!online && <p className="text-xs opacity-80">Absensi tetap bisa ditandai dan akan dikirim otomatis saat sinyal kembali.{fromCache ? " Daftar murid diambil dari data tersimpan." : ""}</p>}
            {online && queue.length > 0 && <p className="font-semibold" data-testid="offline-pending-count">{queue.length} absensi offline menunggu dikirim</p>}
            {online && queue.length === 0 && fromCache && <p className="font-semibold">Menampilkan data tersimpan — tarik ulang untuk menyegarkan.</p>}
          </div>
          {online && queue.length > 0 && (
            <Button size="sm" onClick={sync} disabled={syncing} className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white" data-testid="offline-sync-btn">
              {syncing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />} Kirim Sekarang
            </Button>
          )}
          {!online && queue.length > 0 && <span className="text-xs bg-white/15 rounded-full px-3 py-1" data-testid="offline-pending-count">{queue.length} tersimpan</span>}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 bg-white rounded-2xl p-4 border border-slate-100">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Kelas</span>
          <Select value={kelas} onValueChange={setKelas} disabled={!!wali}>
            <SelectTrigger className="w-28 rounded-xl" data-testid="offline-kelas-select"><SelectValue /></SelectTrigger>
            <SelectContent>{[1, 2, 3, 4, 5, 6].map((k) => <SelectItem key={k} value={String(k)}>Kelas {k}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Tanggal</span>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44 rounded-xl" data-testid="offline-date-input" />
        </div>
        <p className="text-xs text-slate-400 w-full sm:w-auto sm:ml-auto">Ketuk status yang sama sekali lagi untuk <span className="font-medium text-slate-500">menghapus</span> absen.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
      ) : students.length === 0 ? (
        <p className="text-sm text-slate-400 bg-white rounded-2xl p-6 border border-slate-100">Belum ada murid di kelas ini.</p>
      ) : (
        <div className="space-y-2">
          {students.map((s) => {
            const cur = statuses[s.student_id || s.id];
            const sid = s.id;
            const curStatus = statuses[sid];
            return (
              <div key={sid} className="bg-white rounded-2xl p-3 sm:p-4 border border-slate-100 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`offline-row-${sid}`}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-semibold">{s.name[0]}</div>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800 truncate">{s.name} {s.rombel && <span className="text-[10px] text-slate-400 font-normal">· {s.kelas}{s.rombel}</span>}</p>
                    <div className="flex items-center gap-1.5">
                      {curStatus && <span className={`text-[10px] px-2 py-0.5 rounded-full border ${(ATT_STATUS[curStatus] || ATT_STATUS.menunggu).cls}`}>{(ATT_STATUS[curStatus] || { label: curStatus }).label || curStatus}</span>}
                      {pendingHere.some((q) => q.student_id === sid) && <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid={`offline-pending-${sid}`}><Clock className="w-3 h-3" /> Menunggu sinyal</span>}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 sm:w-auto">
                  {QUICK.map((q) => (
                    <Button key={q.v} size="sm" onClick={() => mark(sid, q.v)} disabled={saving === sid + q.v}
                      className={`rounded-lg h-10 ${curStatus === q.v ? q.cls : "bg-slate-100 hover:bg-slate-200 text-slate-600"}`}
                      data-testid={`offline-mark-${q.v}-${sid}`}>
                      <q.icon className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">{q.label}</span>
                    </Button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function QrScan() {
  const todayWib = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" });
  const [date, setDate] = useState(todayWib);
  const [scanning, setScanning] = useState(false);
  const [starting, setStarting] = useState(false);
  const [results, setResults] = useState([]);
  const scannerRef = useRef(null);
  const lastRef = useRef({ code: "", t: 0 });
  const dateRef = useRef(date);
  useEffect(() => { dateRef.current = date; }, [date]);

  const onScan = async (decoded) => {
    const now = Date.now();
    if (decoded === lastRef.current.code && now - lastRef.current.t < 3000) return;
    lastRef.current = { code: decoded, t: now };
    try {
      const res = await api.post("/attendance/scan", { code: decoded, status: "hadir", date: dateRef.current });
      const d = res.data;
      const at = new Date().toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
      if (d.already) toast.info(`${d.student_name} sudah ditandai hadir`);
      else toast.success(`${d.student_name} (Kelas ${d.kelas}) ✓ Hadir`);
      setResults((r) => [{ name: d.student_name, kelas: d.kelas, at, already: d.already }, ...r.filter((x) => x.name !== d.student_name)].slice(0, 100));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kode QR tidak dikenali");
    }
  };

  const start = async () => {
    setStarting(true);
    try {
      const h = new Html5Qrcode("qr-reader");
      scannerRef.current = h;
      await h.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 240, height: 240 } }, onScan, () => {});
      setScanning(true);
    } catch (e) {
      toast.error("Tidak bisa mengakses kamera. Pastikan izin kamera diberikan dan gunakan HTTPS.");
      try { await scannerRef.current?.clear(); } catch (_) { /* ignore */ }
      scannerRef.current = null;
    } finally {
      setStarting(false);
    }
  };

  const stop = async () => {
    const h = scannerRef.current;
    if (h) {
      try { await h.stop(); await h.clear(); } catch (e) { /* ignore */ }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  useEffect(() => () => { stop(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl p-4 border border-slate-100 flex flex-wrap items-center gap-3" data-testid="qr-scan-controls">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Tanggal absen</span>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44 rounded-xl" data-testid="qr-scan-date" />
          {date !== todayWib && <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setDate(todayWib)} data-testid="qr-scan-today-btn">Hari Ini</Button>}
        </div>
        <div className="ml-auto">
          {!scanning ? (
            <Button onClick={start} disabled={starting} className="rounded-xl bg-teal-600 hover:bg-teal-700" data-testid="qr-scan-start-btn">
              {starting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <ScanLine className="w-5 h-5 mr-2" />} Mulai Scan
            </Button>
          ) : (
            <Button onClick={stop} variant="outline" className="rounded-xl border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="qr-scan-stop-btn">
              <Square className="w-5 h-5 mr-2" /> Hentikan
            </Button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-slate-900 rounded-2xl p-3 border border-slate-800">
          {/* #qr-reader must stay empty: html5-qrcode mutates its children directly */}
          <div id="qr-reader" className="rounded-xl overflow-hidden" data-testid="qr-reader" />
          {!scanning && !starting && (
            <div className="text-center text-slate-400 py-16 px-4 min-h-[260px] flex flex-col items-center justify-center" data-testid="qr-reader-placeholder">
              <QrCode className="w-12 h-12 mx-auto mb-3 opacity-60" />
              <p className="text-sm">Arahkan kamera ke kartu QR murid.<br />Tekan <b className="text-slate-200">Mulai Scan</b> untuk menyalakan kamera.</p>
            </div>
          )}
          {scanning && <p className="text-center text-xs text-teal-300 mt-2">Kamera aktif · tahan kartu QR di dalam kotak</p>}
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-4">
          <h3 className="font-display font-semibold text-slate-800 mb-3 flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Tercatat Hadir ({results.length})</h3>
          {results.length === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center">Belum ada murid yang discan.</p>
          ) : (
            <div className="space-y-2 max-h-[340px] overflow-y-auto" data-testid="qr-scan-results">
              {results.map((r, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-emerald-50/60 border border-emerald-100" data-testid={`qr-result-${i}`}>
                  <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-semibold">{r.name?.[0]}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{r.name}</p>
                    <p className="text-xs text-slate-500">Kelas {r.kelas} · {r.at}</p>
                  </div>
                  <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-600 text-white font-medium">{r.already ? "Sudah hadir" : "Hadir"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Attendance() {
  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-800">Absensi</h1>
        <p className="text-slate-500 mt-1">Verifikasi absensi foto (daring), scan QR kartu murid, atau tandai kehadiran langsung saat tatap muka.</p>
      </div>
      <Tabs defaultValue="verify">
        <TabsList className="rounded-xl">
          <TabsTrigger value="verify" className="rounded-lg" data-testid="tab-verify">Verifikasi Foto</TabsTrigger>
          <TabsTrigger value="scan" className="rounded-lg" data-testid="tab-scan">Scan QR</TabsTrigger>
          <TabsTrigger value="offline" className="rounded-lg" data-testid="tab-offline">Absensi Tatap Muka</TabsTrigger>
        </TabsList>
        <TabsContent value="verify" className="mt-6"><PhotoVerify /></TabsContent>
        <TabsContent value="scan" className="mt-6"><QrScan /></TabsContent>
        <TabsContent value="offline" className="mt-6"><OfflineRoster /></TabsContent>
      </Tabs>
    </div>
  );
}
